<template>
  <section>
    <h1 class="title is-1">Reconciliación de inventario</h1>
    <b-breadcrumb align="is-left">
      <b-breadcrumb-item tag="router-link" to="/">Inicio</b-breadcrumb-item>
      <b-breadcrumb-item tag="router-link" to="/inventario">Inventario</b-breadcrumb-item>
      <b-breadcrumb-item active>Reconciliación</b-breadcrumb-item>
    </b-breadcrumb>

    <p class="aviso no-imprimir">
      Escribe la cantidad <strong>física contada</strong> de cada producto. La diferencia se
      calcula sola y se aplica como un <strong>único movimiento de ajuste</strong>, sin costo
      (no afecta la deuda con proveedores). Deja en blanco lo que no cuentes.
    </p>

    <div class="box no-imprimir">
      <b-field label="Buscar producto">
        <b-input v-model="busqueda" placeholder="Nombre o código" icon="magnify" clearable />
      </b-field>

      <div class="resumen">
        <span class="tag is-info">Contados: {{ ajustes.length }}</span>
        <span class="tag is-success">Sobrante: {{ formato(resumen.sobrante) }}</span>
        <span class="tag is-danger">Faltante: {{ formato(resumen.faltante) }}</span>
        <span class="tag" :class="resumen.neto < 0 ? 'is-danger' : 'is-primary'">
          Neto: {{ formato(resumen.neto) }}
        </span>
      </div>

      <b-message v-if="sospechosos.length" type="is-warning" class="mt-3">
        {{ sospechosos.length }} producto(s) con diferencia mayor a {{ LIMITE_AVISO }} unidades.
        Revisa que no sea un error de tecleo: así se descuadró el inventario antes.
      </b-message>

      <div class="botones">
        <b-button icon-left="printer" @click="imprimir">Imprimir planilla</b-button>
        <b-button @click="limpiar" :disabled="!ajustes.length">Limpiar</b-button>
        <b-button type="is-primary" icon-left="check" @click="confirmar"
          :disabled="!ajustes.length" :loading="cargando">
          Aplicar conteo ({{ ajustes.length }})
        </b-button>
      </div>
    </div>

    <div class="encabezado-impresion">
      <strong>Planilla de inventario físico</strong> — {{ fechaImpresion }}
    </div>

    <b-table :data="filas" class="box" :paginated="false" hoverable
      default-sort="nombre" default-sort-direction="asc">
      <b-table-column field="codigo" label="Código" width="120" sortable v-slot="props">
        {{ props.row.codigo }}
      </b-table-column>

      <b-table-column field="nombre" label="Producto" sortable v-slot="props">
        {{ props.row.nombre }}
      </b-table-column>

      <b-table-column field="unidad" label="Unidad" width="90" sortable v-slot="props">
        {{ props.row.unidad }}
      </b-table-column>

      <b-table-column field="existencia" label="Sistema" width="120" sortable numeric v-slot="props">
        {{ formato(props.row.existencia) }}
      </b-table-column>

      <b-table-column label="Físico contado" width="180" v-slot="props">
        <b-input class="no-imprimir" type="number" step="0.01" min="0" size="is-small"
          v-model="conteos[props.row.id]" placeholder="—" />
        <span class="solo-impresion">{{ conteos[props.row.id] || '' }}</span>
      </b-table-column>

      <b-table-column label="Diferencia" width="140" numeric v-slot="props">
        <span :class="claseDiferencia(props.row)">{{ diferencia(props.row) }}</span>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>Sin productos para mostrar.</p>
          </div>
        </section>
      </template>
    </b-table>

    <b-loading :is-full-page="true" v-model="cargando" :can-cancel="false"></b-loading>
  </section>
</template>

<script>
import HttpService from '../../Servicios/HttpService'

// A partir de esta diferencia se avisa en pantalla: es el rango donde un error de
// tecleo (un cero de mas) se lleva el inventario por delante.
const LIMITE_AVISO = 1000

export default {
  name: 'ReconciliacionInventario',

  data: () => ({
    cargando: false,
    busqueda: '',
    productos: [],
    conteos: {},
    LIMITE_AVISO,
  }),

  mounted() {
    this.obtenerProductos()
  },

  computed: {
    filas() {
      const termino = String(this.busqueda || '').trim().toLowerCase()
      if (!termino) return this.productos
      return this.productos.filter(p =>
        String(p.nombre || '').toLowerCase().includes(termino) ||
        String(p.codigo || '').toLowerCase().includes(termino)
      )
    },

    // Solo las filas donde se escribio un conteo
    ajustes() {
      return this.filas
        .filter(p => this.tieneConteo(p))
        .map(p => ({ idProducto: Number(p.id), contado: Number(this.conteos[p.id]) }))
    },

    resumen() {
      let sobrante = 0
      let faltante = 0

      this.filas.forEach(p => {
        if (!this.tieneConteo(p)) return
        const d = Number(this.conteos[p.id]) - Number(p.existencia || 0)
        if (d > 0) sobrante += d
        else faltante += Math.abs(d)
      })

      return { sobrante, faltante, neto: sobrante - faltante }
    },

    sospechosos() {
      return this.filas.filter(p => {
        if (!this.tieneConteo(p)) return false
        return Math.abs(Number(this.conteos[p.id]) - Number(p.existencia || 0)) > LIMITE_AVISO
      })
    },

    fechaImpresion() {
      return new Date().toLocaleString('es-VE')
    },
  },

  methods: {
    async obtenerProductos() {
      this.cargando = true

      const respuesta = await HttpService.obtenerConConsultas('productos.php', {
        accion: 'obtener',
        pagina: 1,
        limite: 0,
      })

      this.productos = (respuesta && respuesta.productos) ? respuesta.productos : []

      // En Vue 2 las claves nuevas no son reactivas: el mapa de conteos se crea
      // completo al cargar para que el v-model funcione.
      const conteos = {}
      this.productos.forEach(p => { conteos[p.id] = '' })
      this.conteos = conteos

      this.cargando = false
    },

    tieneConteo(producto) {
      const valor = this.conteos[producto.id]
      return valor !== '' && valor !== null && valor !== undefined && !isNaN(Number(valor))
    },

    diferencia(producto) {
      if (!this.tieneConteo(producto)) return '—'
      const d = Math.round((Number(this.conteos[producto.id]) - Number(producto.existencia || 0)) * 100) / 100
      return d > 0 ? '+' + d : String(d)
    },

    claseDiferencia(producto) {
      if (!this.tieneConteo(producto)) return 'sin-dato'
      const d = Number(this.conteos[producto.id]) - Number(producto.existencia || 0)
      if (Math.abs(d) < 0.005) return 'igual'
      return d > 0 ? 'sobra' : 'falta'
    },

    formato(valor) {
      return Number(valor || 0).toLocaleString('es-VE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    limpiar() {
      const conteos = {}
      this.productos.forEach(p => { conteos[p.id] = '' })
      this.conteos = conteos
    },

    imprimir() {
      window.print()
    },

    confirmar() {
      this.$buefy.dialog.confirm({
        title: 'Aplicar conteo',
        message: 'Se ajustaran <b>' + this.ajustes.length + '</b> producto(s).<br>' +
          'Sobrante: <b>' + this.formato(this.resumen.sobrante) + '</b><br>' +
          'Faltante: <b>' + this.formato(this.resumen.faltante) + '</b><br><br>' +
          'Queda registrado y no se puede deshacer.',
        confirmText: 'Aplicar',
        cancelText: 'Cancelar',
        type: this.sospechosos.length ? 'is-warning' : 'is-primary',
        onConfirm: () => this.aplicar(),
      })
    },

    async aplicar() {
      this.cargando = true

      try {
        const respuesta = await HttpService.registrar('productos.php', {
          accion: 'reconciliar',
          ajustes: this.ajustes,
        })

        if (respuesta && respuesta.success) {
          this.$buefy.toast.open({
            type: 'is-success',
            duration: 8000,
            message: 'Inventario ajustado: ' + respuesta.productos_ajustados + ' producto(s). ' +
              '+' + respuesta.total_sobrante + ' / -' + respuesta.total_faltante,
          })
          this.limpiar()
          await this.obtenerProductos()
        } else {
          this.$buefy.toast.open({
            type: 'is-danger',
            duration: 10000,
            message: (respuesta && respuesta.error) || 'No se pudo aplicar el conteo',
          })
        }
      } catch (e) {
        this.$buefy.toast.open({ type: 'is-danger', message: 'Error de red al aplicar el conteo' })
      } finally {
        this.cargando = false
      }
    },
  },
}
</script>

<style scoped>
.aviso { margin-bottom: 1rem; }
.resumen { display: flex; gap: .5rem; flex-wrap: wrap; }
.botones { display: flex; gap: .5rem; flex-wrap: wrap; margin-top: 1rem; }
.solo-impresion { display: none; }
.encabezado-impresion { display: none; margin-bottom: .75rem; }
.igual { color: #7a7a7a; }
.sobra { color: #48c78e; font-weight: 600; }
.falta { color: #f14668; font-weight: 600; }
.sin-dato { color: #b5b5b5; }

@media print {
  .no-imprimir,
  .navbar,
  .breadcrumb { display: none !important; }
  .solo-impresion { display: inline; }
  .encabezado-impresion { display: block; }
  .box { box-shadow: none; border: 0; }
}
</style>

